import { WorkplaceRequestElement } from "@gettactic/api";
import { capitalize } from "@gettactic/helpers";
import { Button } from "@v2/components/ui/button";
import { Label } from "@v2/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@v2/components/ui/select";
import { MobileDrawer } from "@v2/mobile/MobileDrawer/MobileDrawer";
import { STATUS_OPTIONS } from "@webV2/components/workplace-request/status-select/types";
import { useState } from "react";
import { Textarea } from "../../../../components/ui/textarea";
import { RequestStatus } from "../workplace-requests-settings/types";
import { StatusDisplay } from "./StatusDisplay";
import { StatusChangeSummary } from "./StatusChangeSummary";

interface MobileStatusSelectWithModalProps {
  request: WorkplaceRequestElement;
  onStatusChange: (
    requestId: string,
    newStatus: RequestStatus,
    comment?: string
  ) => void;
  className?: string;
}

export const MobileStatusSelectWithModal = ({
  request,
  onStatusChange,
  className = "h-8 border-0 bg-transparent text-xs justify-start space-x-1"
}: MobileStatusSelectWithModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<RequestStatus | null>(
    null
  );
  const [comment, setComment] = useState("");

  const handleStatusSelect = (value: string) => {
    const statusMap: Record<string, RequestStatus> = {
      open: "Open",
      pending: "Pending",
      closed: "Closed"
    };
    const newStatus = statusMap[value] as RequestStatus;
    const currentStatus =
      request.status.charAt(0).toUpperCase() + request.status.slice(1);
    if (newStatus === currentStatus) return;

    setPendingStatus(newStatus);
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingStatus) {
      onStatusChange(request.id, pendingStatus, comment.trim());
      setComment("");
      setPendingStatus(null);
      setIsModalOpen(false);
    }
  };

  const handleCancel = () => {
    setComment("");
    setPendingStatus(null);
    setIsModalOpen(false);
  };


  const getDefaultMessage = (currentStatus: string, newStatus: string) => {
    if (newStatus === "Closed") {
      return `Marking request as ${newStatus.toLowerCase()}`;
    }
    if (newStatus === "Pending") {
      return `Moving request to ${newStatus.toLowerCase()}`;
    }
    return `Reopening request as ${newStatus.toLowerCase()}`;
  };

  return (
    <>
      <Select
        value={request.status}
        onValueChange={handleStatusSelect}
      >
        <SelectTrigger className={className}>
          <SelectValue>
            <StatusDisplay status={request.status} />
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <StatusDisplay status={option.label} className="space-x-2" />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <MobileDrawer
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        size="fit"
        dismissible={true}
      >
        <div className="p-6">
          <h3 className="mb-4 font-semibold text-lg text-primary">
            Update Request Status
          </h3>

          <div className="space-y-4">
            {/* Status Change Summary */}
            <StatusChangeSummary
              requestTitle={request.title}
              currentStatus={request.status}
              newStatus={pendingStatus || ""}
            />

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="comment" className="font-medium text-sm">
                  Comment (Optional)
                </Label>
                <Textarea
                  id="comment"
                  placeholder={getDefaultMessage(
                    capitalize(request.status),
                    pendingStatus || ""
                  )}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[80px] resize-none"
                />
              </div>

              <div className="flex flex-col space-y-3">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!pendingStatus}
                >
                  Update Status
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </MobileDrawer>
    </>
  );
};
